import {
  AllowedFanNumbersDto,
  FanNumberingDashboardService,
  InboxSplitterResponseDto,
} from '@api'
import {
  attach,
  createEffect,
  createEvent,
  createStore,
  sample,
} from 'effector'
import { matchPath } from 'react-router'

import { Pages, isCUI } from '@/consts/routes.ts'
import { $creatorId } from '@/models/creators.ts'
import { initializeApp } from '@/models/init.ts'
import { $currentPage } from '@/models/router.ts'
import { toast } from '@/models/toasts'
import { handleError } from '@/utils/error'

export const fetchAllowedFanNumbersFx = createEffect('fetchAllowedFanNumbers', {
  handler: async (creatorId: number) => {
    return FanNumberingDashboardService.getAllowedFanNumbers({
      creatorId,
    })
  },
})

fetchAllowedFanNumbersFx.fail.watch(({ error, params }) =>
  handleError(error, 'Failed to fetch allowed fan numbers', () =>
    fetchAllowedFanNumbersFx(params),
  ),
)

export interface FanNumberToUserIdMap {
  [key: string]: number[]
}

export interface UpdateAllowedFanNumbersParams {
  creatorId: number
  fanNumberToUserId: FanNumberToUserIdMap
}

export const updateAllowedFanNumbersFx = createEffect(
  'updateAllowedFanNumbers',
  {
    handler: async ({
      creatorId,
      fanNumberToUserId,
    }: UpdateAllowedFanNumbersParams) => {
      return FanNumberingDashboardService.setAllowedFanNumbers({
        creatorId,
        requestBody: { fanNumberToUserId },
      })
    },
  },
)

updateAllowedFanNumbersFx.failData.watch((error) =>
  handleError(error, 'Failed to update allowed fan numbers'),
)

export const resetAllowedFanNumbers = createEvent('resetAllowedFanNumbers')

export const $allowedFanNumbers = createStore<AllowedFanNumbersDto | null>(
  null,
  { name: 'allowedFanNumbers' },
)
  .on(fetchAllowedFanNumbersFx.doneData, (_, payload) => payload)
  .reset(resetAllowedFanNumbers)

export const fetchInboxSplitterStatusFx = createEffect(
  'fetchInboxSplitterStatus',
  {
    handler: async (creatorId: number) => {
      return FanNumberingDashboardService.getInboxSplitterStatus({
        creatorId,
      })
    },
  },
)

fetchInboxSplitterStatusFx.fail.watch(({ error, params }) => {
  handleError(error, 'Failed to fetch inbox splitter status', () =>
    fetchInboxSplitterStatusFx(params),
  )
})

export interface UpdateInboxSplitterStatusParams {
  creatorId: number
  enabled: boolean
}

export const updateInboxSplitterStatusFx = attach({
  name: 'updateInboxSplitterStatus',
  source: {
    creatorId: $creatorId,
  },
  mapParams: (enabled: boolean, { creatorId }) => ({
    enabled,
    creatorId: creatorId!,
  }),
  effect: createEffect(
    async ({ creatorId, enabled }: UpdateInboxSplitterStatusParams) => {
      await FanNumberingDashboardService.setInboxSplitterStatus({
        creatorId,
        requestBody: { enabled },
      })
    },
  ),
})

updateInboxSplitterStatusFx.doneData.watch(() => {
  toast.success(
    'Successfully saved. Changes will be applied within a few minutes',
  )
})

updateInboxSplitterStatusFx.failData.watch((error) =>
  handleError(error, 'Failed to update inbox splitter status'),
)

export const $inboxSplitterStatus =
  createStore<InboxSplitterResponseDto | null>(null, {
    name: 'inboxSplitterStatus',
  })
    .on(fetchInboxSplitterStatusFx.doneData, (_, payload) => payload)
    .on(updateInboxSplitterStatusFx.done, (state, { params }) => ({
      ...state,
      inboxSplitterEnabled: params,
    }))

export const startFanNumberingUpdating = createEvent(
  'start fanNumberingUpdating',
)
export const finishFanNumberingUpdating = createEvent(
  'finish fanNumberingUpdating',
)

sample({
  clock: [
    $currentPage,
    initializeApp,
    $creatorId,
    finishFanNumberingUpdating,
    startFanNumberingUpdating,
  ],
  source: { page: $currentPage, creatorId: $creatorId },
  filter: ({ page, creatorId }) => {
    const isFanNumberingPage = matchPath(
      Pages.CREATOR_SETTINGS_FAN_NUMBERING,
      page,
    )

    return Boolean((isFanNumberingPage || isCUI) && creatorId)
  },
  fn: ({ creatorId }) => creatorId!,
  target: [fetchAllowedFanNumbersFx, fetchInboxSplitterStatusFx],
})
